/**
 * Custom Hook: useFacilitiesPageBased
 * Handles page-based pagination for facilities using React Query
 * 
 * Uses the new GET /facilities endpoint with page/limit pagination
 * instead of cursor-based pagination.
 * 
 * Features:
 * - Page-based pagination (page/limit)
 * - Manual "Load More" button support
 * - Proper cache management
 * - Search support
 * - Error handling
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { getFacilities } from '../services/facilitiesService';

/** Query key prefix for facilities page-based list (Step 1). Use for refetch after create/update/delete/duplicate. */
export const FACILITIES_PAGE_BASED_QUERY_KEY = ['facilities-page-based'];

/**
 * @typedef {Object} UseFacilitiesPageBasedParams
 * @property {string} [queryScope] - Isolates cache per context so pagination in one view does not affect another
 * @property {number} [limit=10] - Items per page
 * @property {string} [search] - Search term to filter facilities
 */

/**
 * Custom hook to fetch facilities with page-based pagination
 * 
 * @param {UseFacilitiesPageBasedParams} params - Query parameters
 * @returns {Object} Facilities data and pagination state
 */
export const useFacilitiesPageBased = (params = {}) => {
  const { queryScope = 'default', limit = 10, search = '', enabled = true } = params;

  const scope = typeof queryScope === 'string' && queryScope ? queryScope : 'default';

  const query = useInfiniteQuery({
    queryKey: [...FACILITIES_PAGE_BASED_QUERY_KEY, scope, search, limit],
    queryFn: ({ pageParam = 1, signal }) => {
      return getFacilities({
        page: pageParam,
        limit,
        search,
        signal,
      });
    },
    getNextPageParam: (lastPage) => {
      const { currentPage, hasNextPage } = lastPage.meta.pagination;
      // Return next page number if there's a next page, otherwise undefined
      return hasNextPage ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: enabled, // Only fetch when enabled is true
  });

  // Flatten all pages into a single array
  const facilities = query.data?.pages.flatMap((page) => page.data) ?? [];

  // Get pagination metadata from the last page
  const lastPage = query.data?.pages?.[query.data.pages.length - 1];
  const totalCount = lastPage?.meta?.pagination?.totalItems ?? 0;
  const currentPage = lastPage?.meta?.pagination?.currentPage ?? 1;
  const totalPages = lastPage?.meta?.pagination?.totalPages ?? 1;
  const hasNextPage = query.hasNextPage ?? false;

  // Removed excessive logging to prevent infinite loops
  // Logs are only shown in development when explicitly needed for debugging

  return {
    facilities,
    totalCount,
    currentPage,
    totalPages,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};
